/**
 * Section « À arbitrer » : pièces douteuses et doublons probables. La personne
 * tranche : facture du SCC (la pièce passe à valider), ou écartée avec un motif.
 * Après chaque décision de nature, la règle qu'on en tire est proposée.
 */

import { h } from '../../dom.js';
import { corrigerPiece, ecarterPiece, reglePropose, type NatureEcartee, type PieceListee } from './api.js';
import { apercuPiece, brut, creerRetour, messageDe, resumeProvenance, verrouille, type Contexte } from './communs.js';
import { sectionDeCartes, type ListeCartes, type SectionCartes } from './liste-cartes.js';
import { bandeauRegleProposee } from './pieces-regles.js';
import { MOTIFS_PAR_DEFAUT, NATURES } from './pieces-textes.js';

const NATURES_ECART: readonly NatureEcartee[] = ['autre_structure', 'facture_emise', 'pas_une_facture'];
const MOTIF_DOUBLON = 'Doublon d’une pièce déjà classée';

function carteArbitrer(p: PieceListee, liste: ListeCartes, ctx: Contexte, proposer: (pieceId: number) => void): HTMLElement {
  const retour = creerRetour();
  const motif = h('input', { type: 'text', maxlength: 300, classe: 'champ-motif', placeholder: 'Motif de l’écart' });
  const confirmer = h('button', { type: 'button', classe: 'btn-action principal' }, 'Confirmer l’écart');
  const annuler = h('button', { type: 'button', classe: 'btn-action' }, 'Annuler');
  const etiquetteEcart = h('span', {});
  const formEcart = h('div', { classe: 'ecart-piece', hidden: true }, etiquetteEcart, h('label', {}, 'Motif ', motif), confirmer, annuler);
  /** Nature de l'écart en cours ; `null` : un doublon, sans nature. */
  let natureEcart: NatureEcartee | null = null;

  const facture = h('button', { type: 'button', classe: 'btn-action principal' }, 'C’est une facture du SCC');
  const boutonsEcart = [
    ...NATURES_ECART.map((n) => {
      const b = h('button', { type: 'button', classe: 'btn-action' }, NATURES[n]);
      b.addEventListener('click', () => {
        ouvrirEcart(n, MOTIFS_PAR_DEFAUT[n]);
      });
      return b;
    }),
    (() => {
      const b = h('button', { type: 'button', classe: 'btn-action' }, 'Doublon à écarter');
      b.addEventListener('click', () => {
        ouvrirEcart(null, MOTIF_DOUBLON);
      });
      return b;
    })(),
  ];
  const tous = [facture, ...boutonsEcart, motif, confirmer, annuler];

  function ouvrirEcart(n: NatureEcartee | null, motifParDefaut: string): void {
    natureEcart = n;
    etiquetteEcart.textContent = n === null ? 'Écarter comme doublon. ' : `Écarter : ${NATURES[n].toLowerCase()}. `;
    motif.value = motifParDefaut;
    formEcart.hidden = false;
    motif.focus();
  }

  facture.addEventListener('click', () => {
    void (async () => {
      try {
        retour.effacer();
        await verrouille(tous, () => corrigerPiece(p.id, { nature: 'facture_scc' }));
        liste.retirer(p.id);
        ctx.retour.ok('Pièce reconnue comme facture du SCC : elle est maintenant à valider.');
        ctx.recharger('valider');
        proposer(p.id);
      } catch (e) {
        retour.ko(messageDe(e, 'La décision n’a pas pu être enregistrée.'));
      }
    })();
  });

  annuler.addEventListener('click', () => {
    formEcart.hidden = true;
  });
  confirmer.addEventListener('click', () => {
    const m = motif.value.trim();
    if (m === '') {
      retour.ko('Le motif est obligatoire : il reste dans l’historique.');
      return;
    }
    const nature = natureEcart;
    void (async () => {
      try {
        retour.effacer();
        await verrouille(tous, () => ecarterPiece(p.id, m, nature ?? undefined));
        liste.retirer(p.id);
        ctx.retour.ok('Pièce écartée. Elle reste dans l’historique : on peut la reprendre.');
        ctx.recharger('historique');
        // Un doublon n'apprend rien à une règle ; une nature, si.
        if (nature !== null) proposer(p.id);
      } catch (e) {
        retour.ko(messageDe(e, 'La pièce n’a pas pu être écartée.'));
      }
    })();
  });

  return h(
    'article',
    { classe: 'carte-piece', 'aria-label': `Pièce ${String(p.id)}` },
    h('div', { classe: 'carte-apercu' }, apercuPiece(p)),
    h(
      'div',
      { classe: 'carte-corps' },
      resumeProvenance(p.provenance, Math.max(0, p.nbProvenances - 1)),
      h('p', { classe: 'motif-arbitrage' }, h('strong', {}, 'Pourquoi cette pièce attend : '), brut(p.motif ?? 'le tri n’a pas pu trancher.')),
      h('div', { classe: 'barre-actions' }, facture, ...boutonsEcart),
      formEcart,
      retour.el,
    ),
  );
}

export function sectionArbitrer(ctx: Contexte): SectionCartes {
  const propositions = h('div', { classe: 'propositions-regles', 'aria-live': 'polite' });

  /** Affiche la règle tirée de la pièce arbitrée ; un échec de la proposition n'empêche rien. */
  const proposer = (pieceId: number): void => {
    void (async () => {
      try {
        const { regle } = await reglePropose(pieceId);
        const bandeau = bandeauRegleProposee(regle, ctx, () => {
          bandeau.remove();
        });
        propositions.appendChild(bandeau);
      } catch {
        /* Pas de règle à proposer : la décision, elle, est enregistrée. */
      }
    })();
  };

  return sectionDeCartes(
    2,
    'À arbitrer',
    'Douteuses ou doublons probables : à vous de trancher',
    'a_arbitrer',
    (p, liste) => carteArbitrer(p, liste, ctx, proposer),
    'Aucune pièce à arbitrer.',
    propositions,
  );
}
